/**
 * Structured, Level-based logger with PII masking and telemetry output hooks.
 * Designed for enterprise environments, avoiding console pollution while offering deep tracing.
 */

export type LogLevel = 'DEBUG' | 'INFO' | 'WARN' | 'ERROR';

export interface LogEntry {
  timestamp: string;
  level: LogLevel;
  context: string;
  message: string;
  metadata?: Record<string, any>;
  error?: {
    message: string;
    stack?: string;
    code?: string | number;
  };
}

class EnterpriseLogger {
  private isDevelopment = true;
  private logsBuffer: LogEntry[] = [];
  private maxBufferSize = 250;
  private telemetryCallback: ((entry: LogEntry) => void) | null = null;
  private currentLevel: LogLevel = 'INFO';
  private isSilent = false;

  // Throttling / Deduplication limits
  private recentLogs = new Map<string, { timestamp: number; count: number }>();
  private throttleIntervalMs = 4000; // 4 seconds debounce for identical messages

  // Sensitive fields to sanitize to preserve privacy and safety rules
  private blacklistedKeys = ['password', 'token', 'apiKey', 'secret', 'key', 'senha', 'token_acesso'];

  private levelPriorities: Record<LogLevel, number> = {
    DEBUG: 0,
    INFO: 1,
    WARN: 2,
    ERROR: 3,
  };

  constructor() {
    this.isDevelopment = typeof import.meta !== "undefined" && (import.meta as any).env ? (import.meta as any).env.DEV : true;
    
    // Safe standard initialization on environment/options loading
    if (typeof window !== 'undefined') {
      try {
        const savedLevel = localStorage.getItem('wm_log_level') as LogLevel;
        if (savedLevel && ['DEBUG', 'INFO', 'WARN', 'ERROR'].includes(savedLevel)) {
          this.currentLevel = savedLevel;
        } else {
          this.currentLevel = 'WARN';
        }

        const silentFlag = localStorage.getItem('wm_log_silent');
        this.isSilent = silentFlag === 'true';
      } catch (err) {
        // Fallback for storage restrictions
        this.currentLevel = 'INFO';
        this.isSilent = false;
      }
    }
  }

  /**
   * Sets the active logging level.
   */
  public setLevel(level: LogLevel): void {
    this.currentLevel = level;
    if (typeof window !== 'undefined') {
      try {
        localStorage.setItem('wm_log_level', level);
      } catch {}
    }
  }

  /**
   * Mutes or unmutes the logger completely.
   */
  public setSilent(silent: boolean): void {
    this.isSilent = silent;
    if (typeof window !== 'undefined') {
      try {
        localStorage.setItem('wm_log_silent', String(silent));
      } catch {}
    }
  }

  /**
   * Registers a telemetry pipeline callback (e.g. Sentry, Datadog, or custom WebSocket relay).
   */
  public registerTelemetrySink(callback: (entry: LogEntry) => void): void {
    this.telemetryCallback = callback;
  }

  public debug(context: string, message: string, metadata?: Record<string, any>): void {
    this.log('DEBUG', context, message, metadata);
  }

  public info(context: string, message: string, metadata?: Record<string, any>): void {
    this.log('INFO', context, message, metadata);
  }

  public warn(context: string, message: string, metadata?: Record<string, any>): void {
    this.log('WARN', context, message, metadata);
  }

  public error(context: string, message: string, error?: any, metadata?: Record<string, any>): void {
    this.log('ERROR', context, message, metadata, error);
  }

  /**
   * Core logging mechanism. Safely formats, sanitizes, and distributes log payloads.
   */
  private log(
    level: LogLevel,
    context: string,
    message: string,
    metadata?: Record<string, any>,
    error?: any
  ): void {
    // 1. Silent Check
    if (this.isSilent) {
      return;
    }

    // 2. Leve/Priority matching check
    const currentPriority = this.levelPriorities[this.currentLevel];
    const logPriority = this.levelPriorities[level];
    if (logPriority < currentPriority) {
      return;
    }

    // 3. Deduplication and Throttling Strategy to protect AI Studio frame performance
    const now = Date.now();
    const cleanMsg = message ? message.trim() : '';
    const throttleKey = `${level}:${context}:${cleanMsg}`;

    if (this.recentLogs.size > 800) {
      this.recentLogs.clear(); // Safe clean prune
    }

    const previousLog = this.recentLogs.get(throttleKey);
    let effectiveMessage = message;

    if (previousLog) {
      const elapsed = now - previousLog.timestamp;
      if (elapsed < this.throttleIntervalMs) {
        previousLog.count++;
        return; // Suppress from output to avoid heavy frame lags
      } else {
        const suppressedCount = previousLog.count;
        this.recentLogs.set(throttleKey, { timestamp: now, count: 0 });
        if (suppressedCount > 0) {
          effectiveMessage = `${message} [suppressed ${suppressedCount} duplicate events in last ${Math.round(elapsed / 1000)}s]`;
        }
      }
    } else {
      this.recentLogs.set(throttleKey, { timestamp: now, count: 0 });
    }

    // 4. Sanitize and normal parsing
    const sanitizedMetadata = metadata ? this.sanitize(metadata) : undefined;
    const parsedError = error ? this.normalizeError(error) : undefined;

    const entry: LogEntry = {
      timestamp: new Date().toISOString(),
      level,
      context: `[${context.toUpperCase()}]`,
      message: effectiveMessage,
      metadata: sanitizedMetadata,
      error: parsedError,
    };

    // Keep log in circular runtime buffer
    this.logsBuffer.push(entry);
    if (this.logsBuffer.length > this.maxBufferSize) {
      this.logsBuffer.shift();
    }

    // Execute active outer sinks
    if (this.telemetryCallback) {
      try {
        this.telemetryCallback(entry);
      } catch (err) {
        // Prevent telemetry errors from breaking the main UX flow
        console.error('[EnterpriseLogger] Telemetry sink failed:', err);
      }
    }

    // Output to console in development environment
    if (this.isDevelopment) {
      this.printToConsole(entry);
    }
  }

  /**
   * Safe JSON-structured printer mapping browser consoles cleanly.
   */
  private printToConsole(entry: LogEntry): void {
    const consoleArgs = [
      `%c${entry.timestamp} %c${entry.level} %c${entry.context} %c${entry.message}`,
      'color: #94a3b8; font-size: 10px;',
      this.getLevelConsoleStyle(entry.level),
      'color: #38bdf8; font-weight: bold;',
      'color: inherit;'
    ];

    const hasMeaningfulMetadata = entry.metadata && Object.keys(entry.metadata).some(key => {
      const val = entry.metadata![key];
      if (val === undefined || val === null) return false;
      if (typeof val === 'object' && Object.keys(val).length === 0) return false;
      return true;
    });

    if (hasMeaningfulMetadata) {
      consoleArgs.push('\nMetadata:', entry.metadata as any);
    }

    if (entry.error && (entry.error.message || entry.error.code || entry.error.stack)) {
      consoleArgs.push('\nError Info:', entry.error as any);
    }

    if (entry.level === 'ERROR') {
      console.warn(...consoleArgs);
    } else if (entry.level === 'WARN') {
      console.warn(...consoleArgs);
    } else {
      console.log(...consoleArgs);
    }
  }

  private getLevelConsoleStyle(level: LogLevel): string {
    switch (level) {
      case 'DEBUG':
        return 'background: #334155; color: #cbd5e1; padding: 1px 4px; border-radius: 2px; font-weight: bold; font-size: 10px;';
      case 'INFO':
        return 'background: #0369a1; color: #f0f9ff; padding: 1px 4px; border-radius: 2px; font-weight: bold; font-size: 10px;';
      case 'WARN':
        return 'background: #b45309; color: #fef3c7; padding: 1px 4px; border-radius: 2px; font-weight: bold; font-size: 10px;';
      case 'ERROR':
        return 'background: #be123c; color: #fff1f2; padding: 1px 4px; border-radius: 2px; font-weight: bold; font-size: 10px;';
    }
  }

  /**
   * Normalizes mixed error objects into consistent flat models.
   */
  private normalizeError(error: any): LogEntry['error'] {
    if (error instanceof Error) {
      return {
        message: error.message,
        stack: error.stack,
        code: (error as any).code || (error as any).status,
      };
    }
    
    if (typeof error === 'string') {
      return { message: error };
    }

    return {
      message: error?.message || 'Unknown Error structure',
      code: error?.code || error?.status,
      stack: error?.stack,
    };
  }

  /**
   * Filters out high-risk inputs and values recursively to comply with security requirements.
   */
  private sanitize(obj: Record<string, any>): Record<string, any> {
    const copy = { ...obj };
    for (const key in copy) {
      if (typeof copy[key] === 'object' && copy[key] !== null) {
        copy[key] = this.sanitize(copy[key]);
      } else if (this.blacklistedKeys.some(k => key.toLowerCase().includes(k))) {
        copy[key] = '[MASKED_SENSITIVE_DATA]';
      }
    }
    return copy;
  }

  /**
   * Retrieve active buffers.
   */
  public getLogs(): LogEntry[] {
    return [...this.logsBuffer];
  }

  /**
   * Clean logged arrays.
   */
  public clearLogs(): void {
    this.logsBuffer = [];
  }
}

export const logger = new EnterpriseLogger();
export default logger;
